import { type Resource, createInstance } from "i18next";
import { type PropsWithChildren, useMemo } from "react";
import { I18nextProvider, initReactI18next } from "react-i18next";

import en from "./locales/en.json";
import ru from "./locales/ru.json";
import type { TranslationResources } from "./type";

export interface TranslationProviderOptions {
  locale?: string;
  resources?: TranslationResources;
}

const DEFAULT_RESOURCES: TranslationResources = {
  en,
  ru,
};

function toI18nextResources(resources?: TranslationResources): Resource {
  const locales = new Set([
    ...Object.keys(DEFAULT_RESOURCES),
    ...Object.keys(resources ?? {}),
  ]);

  return Object.fromEntries(
    Array.from(locales).map((locale) => [
      locale,
      {
        translation: {
          ...(DEFAULT_RESOURCES[locale] ?? {}),
          ...(resources?.[locale] ?? {}),
        },
      },
    ]),
  );
}

export function TranslationProvider({
  children,
  locale = "en",
  resources,
}: PropsWithChildren<TranslationProviderOptions>) {
  const i18n = useMemo(() => {
    const instance = createInstance();
    instance.use(initReactI18next);
    instance.init({
      fallbackLng: "en",
      initAsync: false,
      interpolation: {
        escapeValue: false,
        prefix: "{",
        suffix: "}",
      },
      lng: locale,
      react: {
        useSuspense: false,
      },
      resources: toI18nextResources(resources),
    });
    return instance;
  }, [locale, resources]);

  return <I18nextProvider i18n={i18n}>{children}</I18nextProvider>;
}
